import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { usePrevious } from 'helpers/react-hooks';
import Segment from 'helpers/segment';
import { selectActiveVenueId } from 'selectors/general';
import { selectIsMobile } from 'selectors/ui';

import { Card } from 'components/Card';
import Callout from 'components/Callout';
import { DayHourHeatmapPlaceholder } from 'components/DayHourHeatmap';
import { Dropdown, Menu } from 'components/Dropdown';
import ExpandableSection, { EXPANDABLE_SECTION_VARIANTS } from 'components/ExpandableSection';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';

import Heatmap from './Heatmap';
import MobileTooltipSheet from './MobileTooltipSheet';
import messages from './WeeklyEarnings.messages';
import { CHART_METRIC, persistClassId, readPersistedClassId } from './WeeklyEarnings.helpers';
import { useVenueClassesQuery, useWeeklyEarningsQuery } from './WeeklyEarnings.hooks';

import './WeeklyEarnings.scss';

const { block, element } = bem('weekly-earnings');

export const WEEKLY_EARNINGS_LAYOUT = Object.freeze({
  CARD: 'card',
  EMBEDDED: 'embedded',
});

const messageShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  defaultMessage: PropTypes.string.isRequired,
});

const BANNER_VARIANT = Object.freeze({
  OPPORTUNITIES: 'opportunities',
  CAMPAIGN: 'campaign',
});

const SECTION_STATE = Object.freeze({
  LOADING: 'loading',
  ERROR: 'error',
  SELECT_CLASS_TYPE: 'selectClassType',
  INSUFFICIENT_RESERVATIONS: 'insufficientReservations',
  NOT_ENOUGH_HISTORY: 'notEnoughHistory',
  EMPTY: 'empty',
  READY: 'ready',
});

const PLACEHOLDER_TIME_LABELS = ['5 AM', '6 AM', '7 AM', '8 AM', '9 AM', '4 PM', '5 PM', '6 PM', '7 PM'];

const OVERLAY_MESSAGE_BY_STATE = {
  [SECTION_STATE.ERROR]: messages.errorMessage,
  [SECTION_STATE.SELECT_CLASS_TYPE]: messages.selectClassTypeMessage,
  [SECTION_STATE.INSUFFICIENT_RESERVATIONS]: messages.insufficientReservationsMessage,
  [SECTION_STATE.NOT_ENOUGH_HISTORY]: messages.notEnoughHistoryMessage,
  [SECTION_STATE.EMPTY]: messages.emptyMessage,
};

function getSectionState({ isLoading, error, data, hasSelectedClass }) {
  if (isLoading) return SECTION_STATE.LOADING;
  if (error) return SECTION_STATE.ERROR;
  if (!data) return SECTION_STATE.EMPTY;
  if (data.mustSelectClassType && !hasSelectedClass) return SECTION_STATE.SELECT_CLASS_TYPE;
  if (!data.hasQualifyingReservationCount) return SECTION_STATE.INSUFFICIENT_RESERVATIONS;
  if (!data.hasMoreThan8WeeksData) return SECTION_STATE.NOT_ENOUGH_HISTORY;
  return SECTION_STATE.READY;
}

function ExternalLink({ href, children }) {
  return (
    <a className={element('inline-link')} href={href} rel="noopener noreferrer" target="_blank">
      {children}
    </a>
  );
}
ExternalLink.propTypes = { href: PropTypes.string.isRequired, children: PropTypes.node };

function ChartHeader({ showPeriodInHeader }) {
  return (
    <header className={element('header')}>
      <Message as="h2" className={element('title')} message={messages.title} />
      {showPeriodInHeader && <Message as="span" className={element('period')} message={messages.periodLabel} />}
    </header>
  );
}
ChartHeader.propTypes = { showPeriodInHeader: PropTypes.bool };

function PeriodAboveChart() {
  return <Message as="span" className={element('period-above-chart')} message={messages.periodLabel} />;
}

function ChartBanner({ variant, upcomingOpportunitiesCount, opportunitiesPath, campaignsPath, onCtaClick }) {
  if (variant === BANNER_VARIANT.CAMPAIGN) {
    return (
      <Callout
        className={element('banner')}
        icon={<Isvg aria-hidden className={element('banner-icon')} src={ICONS.bulb} />}
        variant="info">
        <Message
          as="p"
          className={element('banner-text')}
          message={messages.bannerCampaignTip}
          values={{
            a: text => (
              <Link className={element('inline-link')} onClick={() => onCtaClick('Campaigns')} to={campaignsPath}>
                {text}
              </Link>
            ),
          }}
        />
      </Callout>
    );
  }

  if (!upcomingOpportunitiesCount) return null;

  return (
    <Callout
      className={element('banner')}
      icon={<Isvg aria-hidden className={element('banner-icon')} src={ICONS.bulb} />}
      variant="info">
      <Message
        as="p"
        className={element('banner-text')}
        message={messages.bannerTip}
        values={{
          count: upcomingOpportunitiesCount,
          a: text => (
            <Link className={element('inline-link')} onClick={() => onCtaClick('Opportunities')} to={opportunitiesPath}>
              {text}
            </Link>
          ),
        }}
      />
    </Callout>
  );
}
ChartBanner.propTypes = {
  variant: PropTypes.oneOf(Object.values(BANNER_VARIANT)).isRequired,
  upcomingOpportunitiesCount: PropTypes.number,
  opportunitiesPath: PropTypes.string.isRequired,
  campaignsPath: PropTypes.string.isRequired,
  onCtaClick: PropTypes.func.isRequired,
};

const ALL_TYPES_KEY = 'all';

function ClassTypesToggle({ toggleMenu, isOpen, disabled, valueText, needsSelection }) {
  return (
    <button
      aria-expanded={isOpen}
      className={element('class-types-trigger', { open: isOpen, highlighted: needsSelection })}
      disabled={disabled}
      onClick={toggleMenu}
      type="button">
      <Message as="span" className={element('class-types-label')} message={messages.classTypesFilterLabel} />
      <span className={element('class-types-value')}>{valueText}</span>
      <SvgArrow
        className={element('class-types-caret')}
        direction={isOpen ? 'up' : 'down'}
        theme={disabled ? 'disabled' : 'dark'}
      />
    </button>
  );
}
ClassTypesToggle.propTypes = {
  toggleMenu: PropTypes.func,
  isOpen: PropTypes.bool,
  disabled: PropTypes.bool,
  valueText: PropTypes.string.isRequired,
  needsSelection: PropTypes.bool,
};

function ClassTypesFilter({ classes, selectedClassId, onChange, mustSelectClassType, isLoading }) {
  const intl = useIntl();

  const classItems = classes.map(classItem => ({ key: classItem.classId, label: classItem.className }));
  const items = mustSelectClassType
    ? classItems
    : [{ key: ALL_TYPES_KEY, label: intl.formatMessage(messages.classTypesFilterAllOption) }, ...classItems];

  const selectedKey = selectedClassId == null ? ALL_TYPES_KEY : selectedClassId;
  const needsSelection = mustSelectClassType && selectedClassId == null;
  const isDisabled = isLoading || items.length === 0;
  const selectedItem = items.find(item => item.key === selectedKey);
  const valueText = selectedItem ? selectedItem.label : intl.formatMessage(messages.classTypesFilterSelectPrompt);

  return (
    <Dropdown
      attachment="left"
      className={element('class-types', { highlighted: needsSelection })}
      contentClassName={element('class-types-menu')}
      disabled={isDisabled}
      getKey="key"
      getText="label"
      isSelected={item => item.key === selectedKey}
      items={items}
      needsSelection={needsSelection}
      onSelect={item => onChange(item.key === ALL_TYPES_KEY ? null : item.key)}
      Content={Menu}
      Toggle={ClassTypesToggle}
      valueText={valueText}
    />
  );
}
ClassTypesFilter.propTypes = {
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      classId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      className: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedClassId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  mustSelectClassType: PropTypes.bool,
  isLoading: PropTypes.bool,
};

function MetricToggle({ metric, onChange }) {
  const renderOption = (value, message) => {
    const isActive = metric === value;
    return (
      <button
        aria-pressed={isActive}
        className={element('metric-toggle-option', { active: isActive })}
        key={value}
        onClick={() => onChange(value)}
        type="button">
        <Message message={message} />
      </button>
    );
  };

  return (
    <div className={element('metric-toggle')} role="group">
      {renderOption(CHART_METRIC.TOTAL_EARNINGS, messages.metricTotalEarnings)}
      {renderOption(CHART_METRIC.AVERAGE_PAYOUT_RATE, messages.metricAveragePayoutRate)}
    </div>
  );
}
MetricToggle.propTypes = {
  metric: PropTypes.oneOf(Object.values(CHART_METRIC)).isRequired,
  onChange: PropTypes.func.isRequired,
};

function ChartFilters({
  metric,
  onMetricChange,
  classes,
  selectedClassId,
  onClassChange,
  mustSelectClassType,
  isClassesLoading,
}) {
  return (
    <div className={element('filters')}>
      <ClassTypesFilter
        classes={classes}
        isLoading={isClassesLoading}
        mustSelectClassType={mustSelectClassType}
        onChange={onClassChange}
        selectedClassId={selectedClassId}
      />
      <MetricToggle metric={metric} onChange={onMetricChange} />
    </div>
  );
}
ChartFilters.propTypes = {
  metric: PropTypes.oneOf(Object.values(CHART_METRIC)).isRequired,
  onMetricChange: PropTypes.func.isRequired,
  classes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedClassId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClassChange: PropTypes.func.isRequired,
  mustSelectClassType: PropTypes.bool,
  isClassesLoading: PropTypes.bool,
};

function HeatmapPlaceholder({ pulse }) {
  return (
    <DayHourHeatmapPlaceholder
      className={element('heatmap-table', { placeholder: true, pulse })}
      pulse={pulse}
      renderEmptyCell={() => <div className={element('cell', { placeholder: true })} />}
      testId="weekly-earnings-placeholder"
      timeLabels={PLACEHOLDER_TIME_LABELS}
    />
  );
}
HeatmapPlaceholder.propTypes = { pulse: PropTypes.bool };

function ChartOverlay({ message }) {
  return (
    <div className={element('overlay')} role="status">
      <Isvg aria-hidden className={element('overlay-icon')} src={ICONS.info} />
      <Message as="span" className={element('overlay-text')} message={message} />
    </div>
  );
}
ChartOverlay.propTypes = { message: messageShape.isRequired };

function ChartExpandableLegend({ opportunitiesPath, campaignsPath, venueId }) {
  const [isOpen, setIsOpen] = useState(true);

  const handleToggle = next => {
    setIsOpen(next);
    if (next) {
      Segment.event('Smartrate faq expanded', {
        faq_name: 'understanding_your_earnings',
        venue_id: venueId,
      });
    }
  };

  const handleCtaClick = type => {
    Segment.event('Click SmartRate Understanding Banner CTA', {
      type,
      venue_id: venueId,
    });
  };

  return (
    <ExpandableSection
      className={element('legend')}
      id="understand-your-earnings"
      isOpen={isOpen}
      onToggle={handleToggle}
      title={messages.legendTitle}
      variant={EXPANDABLE_SECTION_VARIANTS.GRAY}>
      <Message as="p" className={element('legend-intro')} message={messages.legendIntro} />
      <ul className={element('legend-list')}>
        <li>
          <Message message={messages.legendBulletLower} />
        </li>
        <li>
          <Message
            message={messages.legendBulletHigher}
            values={{
              a: text => (
                <Link
                  className={element('inline-link')}
                  onClick={() => handleCtaClick('Opportunities')}
                  to={opportunitiesPath}>
                  {text}
                </Link>
              ),
            }}
          />
        </li>
        <li>
          <Message
            message={messages.legendBulletLow}
            values={{
              a: text => (
                <Link className={element('inline-link')} onClick={() => handleCtaClick('Campaigns')} to={campaignsPath}>
                  {text}
                </Link>
              ),
            }}
          />
        </li>
      </ul>
    </ExpandableSection>
  );
}
ChartExpandableLegend.propTypes = {
  campaignsPath: PropTypes.string.isRequired,
  opportunitiesPath: PropTypes.string.isRequired,
  venueId: PropTypes.number.isRequired,
};

function Footnote({ reservationsPath }) {
  return (
    <Message
      as="p"
      className={element('footnote')}
      message={messages.footnote}
      values={{
        a: text => <ExternalLink href={reservationsPath}>{text}</ExternalLink>,
      }}
    />
  );
}
Footnote.propTypes = { reservationsPath: PropTypes.string.isRequired };

const WeeklyEarnings = ({
  data: dataProp,
  error: errorProp,
  isLoading: isLoadingProp,
  layout = WEEKLY_EARNINGS_LAYOUT.CARD,
}) => {
  const venueId = useSelector(selectActiveVenueId);
  const isMobile = useSelector(selectIsMobile);
  const prevVenueId = usePrevious(venueId);
  const [metric, setMetric] = useState(CHART_METRIC.TOTAL_EARNINGS);
  const [selectedClassId, setSelectedClassId] = useState(() => readPersistedClassId(venueId));
  const [mobileSheet, setMobileSheet] = useState(null);
  const lastMobileSheet = useRef(null);
  if (mobileSheet) lastMobileSheet.current = mobileSheet;

  useEffect(() => {
    if (prevVenueId !== undefined && prevVenueId !== venueId) {
      setSelectedClassId(null);
    }
  }, [venueId]); // eslint-disable-line react-hooks/exhaustive-deps

  const classIds = selectedClassId == null ? [] : [selectedClassId];
  const queryOptions = { enabled: !!venueId && !dataProp && typeof isLoadingProp !== 'boolean' && !errorProp };

  const totalEarningsQuery = useWeeklyEarningsQuery(
    { venueId, classIds, metric: CHART_METRIC.TOTAL_EARNINGS },
    queryOptions
  );
  const payoutRateQuery = useWeeklyEarningsQuery(
    { venueId, classIds, metric: CHART_METRIC.AVERAGE_PAYOUT_RATE },
    queryOptions
  );

  const classesQuery = useVenueClassesQuery(venueId, { enabled: !!venueId && !dataProp });

  const opportunitiesPath = venueId ? makePath.opportunities(venueId) : '#';
  const campaignsPath = venueId ? makePath.campaigns({ venueId }) : '#';
  const reservationsPath = '/stats/reports/reservations';

  const activeQuery = metric === CHART_METRIC.TOTAL_EARNINGS ? totalEarningsQuery : payoutRateQuery;
  const data = dataProp || activeQuery.data;
  const error = errorProp || activeQuery.error;
  const isLoading =
    typeof isLoadingProp === 'boolean' ? isLoadingProp : !dataProp && !errorProp && activeQuery.isLoading;
  const payoutRateRows = (payoutRateQuery.data || dataProp)?.rows ?? null;
  const totalEarningsRows = (totalEarningsQuery.data || dataProp)?.rows ?? null;
  const classes = classesQuery.data || [];
  const mustSelectClassType = !!data?.mustSelectClassType;
  const classSelectionIsValid =
    selectedClassId != null && classes.some(c => String(c.classId) === String(selectedClassId));

  useEffect(() => {
    if (!classSelectionIsValid) {
      persistClassId(venueId, null);
      setSelectedClassId(null);
    }
  }, [selectedClassId, classes, classSelectionIsValid]);

  const handleClassChange = classId => {
    setSelectedClassId(classId);
    persistClassId(venueId, classId);
    Segment.event('Smartrate chart interacted', {
      interaction_type: 'class_type_filter',
      venue_id: venueId,
    });
    Segment.event('Click SmartRate Class Filter', {
      venue_id: venueId,
    });
  };

  const handleMetricChange = newMetric => {
    setMetric(newMetric);
    Segment.event('Smartrate chart interacted', {
      interaction_type: 'metric_tab',
      venue_id: venueId,
    });
    Segment.event('Click SmartRate Heatmap Type', {
      type: newMetric === CHART_METRIC.AVERAGE_PAYOUT_RATE ? 'Average payout rate' : 'Total earnings',
      venue_id: venueId,
    });
  };

  const handleCellTap = ({ tooltip, campaignsPath: cp, opportunitiesPath: op, classLabel: cl }) => {
    setMobileSheet({ tooltip, campaignsPath: cp, opportunitiesPath: op, classLabel: cl });
  };

  const sectionState = getSectionState({
    isLoading,
    error,
    data,
    hasSelectedClass: mustSelectClassType ? classSelectionIsValid : selectedClassId != null,
  });

  useEffect(() => {
    if (window.location.hash === '#understand-your-earnings') {
      document.getElementById('understand-your-earnings')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [sectionState]);

  useEffect(() => {
    if (venueId && sectionState === SECTION_STATE.INSUFFICIENT_RESERVATIONS) {
      Segment.event('SmartRate Heat-map No data', {
        venue_id: venueId,
      });
    }
  }, [venueId, sectionState]);

  const handleBannerCtaClick = type => {
    Segment.event('Click SmartRate Heatmap Banner CTA', {
      type,
      venue_id: venueId,
    });
  };

  const renderBanner = () => {
    if (sectionState === SECTION_STATE.READY || sectionState === SECTION_STATE.SELECT_CLASS_TYPE) {
      return (
        <ChartBanner
          campaignsPath={campaignsPath}
          onCtaClick={handleBannerCtaClick}
          opportunitiesPath={opportunitiesPath}
          upcomingOpportunitiesCount={data ? data.upcomingOpportunitiesCount : 0}
          variant={BANNER_VARIANT.OPPORTUNITIES}
        />
      );
    }

    if (sectionState === SECTION_STATE.INSUFFICIENT_RESERVATIONS || sectionState === SECTION_STATE.NOT_ENOUGH_HISTORY) {
      return (
        <ChartBanner
          campaignsPath={campaignsPath}
          onCtaClick={handleBannerCtaClick}
          opportunitiesPath={opportunitiesPath}
          variant={BANNER_VARIANT.CAMPAIGN}
        />
      );
    }

    return null;
  };

  const renderChart = () => {
    if (sectionState === SECTION_STATE.READY) {
      return (
        <>
          <Heatmap
            campaignsPath={campaignsPath}
            classLabel={
              selectedClassId == null ? null : classes.find(c => c.classId === selectedClassId)?.className ?? null
            }
            currencyCode={data.currency}
            isMobile={isMobile}
            onCellTap={handleCellTap}
            onCloseMobileSheet={() => setMobileSheet(null)}
            opportunitiesPath={opportunitiesPath}
            payoutRateRows={payoutRateRows}
            rows={data.rows}
            totalEarningsRows={totalEarningsRows}
            venueId={venueId}
          />
          <MobileTooltipSheet
            campaignsPath={lastMobileSheet.current?.campaignsPath}
            classLabel={lastMobileSheet.current?.classLabel}
            isOpen={!!mobileSheet}
            onClose={() => setMobileSheet(null)}
            opportunitiesPath={lastMobileSheet.current?.opportunitiesPath}
            tooltip={lastMobileSheet.current?.tooltip}
            venueId={venueId}
          />
        </>
      );
    }

    const overlayMessage = OVERLAY_MESSAGE_BY_STATE[sectionState];

    return (
      <div className={element('chart')}>
        <HeatmapPlaceholder pulse={sectionState === SECTION_STATE.LOADING} />
        {overlayMessage && <ChartOverlay message={overlayMessage} />}
      </div>
    );
  };

  const isEmbedded = layout === WEEKLY_EARNINGS_LAYOUT.EMBEDDED;

  const content = (
    <>
      <ChartHeader showPeriodInHeader={!isEmbedded} />
      {renderBanner()}
      <ChartFilters
        classes={classes}
        isClassesLoading={classesQuery.isLoading}
        metric={metric}
        mustSelectClassType={mustSelectClassType}
        onClassChange={handleClassChange}
        onMetricChange={handleMetricChange}
        selectedClassId={selectedClassId}
      />
      {isEmbedded && <PeriodAboveChart />}
      {renderChart()}
      <ChartExpandableLegend campaignsPath={campaignsPath} opportunitiesPath={opportunitiesPath} venueId={venueId} />
      <Footnote reservationsPath={reservationsPath} />
    </>
  );

  if (isEmbedded) {
    return <section className={block({ embedded: true })}>{content}</section>;
  }

  return <Card className={block()}>{content}</Card>;
};

WeeklyEarnings.propTypes = {
  data: PropTypes.shape({
    currency: PropTypes.string.isRequired,
    hasMoreThan8WeeksData: PropTypes.bool.isRequired,
    hasQualifyingReservationCount: PropTypes.bool.isRequired,
    mustSelectClassType: PropTypes.bool.isRequired,
    upcomingOpportunitiesCount: PropTypes.number,
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        time: PropTypes.string.isRequired,
        cells: PropTypes.arrayOf(PropTypes.shape({ state: PropTypes.string.isRequired })).isRequired,
      })
    ).isRequired,
  }),
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({})]),
  isLoading: PropTypes.bool,
  layout: PropTypes.oneOf(Object.values(WEEKLY_EARNINGS_LAYOUT)),
};

export default WeeklyEarnings;
